/**
 * Version checker component - detects when LIVE is serving old code.
 * Shows a non-intrusive banner when a new version is available.
 */
import React, { useState, useEffect } from 'react';
import { RefreshCw } from 'lucide-react';

// Build ID embedded at build time
const EMBEDDED_BUILD_ID = process.env.REACT_APP_BUILD_ID || 'dev-build';
const API = process.env.REACT_APP_BACKEND_URL;

export const VersionChecker = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [serverVersion, setServerVersion] = useState(null);

  useEffect(() => {
    const checkVersion = async () => {
      try {
        const response = await fetch(`${API}/api/version`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setServerVersion(data.build_id);
          
          // Only show banner if we have a real build ID mismatch
          // Skip if either is 'dev' or 'dev-build'
          if (EMBEDDED_BUILD_ID && 
              !EMBEDDED_BUILD_ID.startsWith('dev') && 
              data.build_id && 
              !data.build_id.startsWith('dev') &&
              EMBEDDED_BUILD_ID !== data.build_id) {
            console.warn(`[VersionChecker] Version mismatch! Client: ${EMBEDDED_BUILD_ID}, Server: ${data.build_id}`);
            setShowBanner(true);
          }
        }
      } catch (error) {
        // Silently fail - don't disrupt user experience
        console.debug('[VersionChecker] Could not check version:', error.message);
      }
    };

    // Check on mount
    checkVersion();
    
    // Check every 5 minutes
    const interval = setInterval(checkVersion, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const handleReload = () => {
    // Force hard reload bypassing cache
    window.location.reload(true);
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-4 right-4 bg-amber-50 border border-amber-200 rounded-lg shadow-lg p-4 max-w-sm z-50 animate-in slide-in-from-bottom-4"
      role="alert"
    >
      <div className="flex items-start gap-3">
        <RefreshCw className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <p className="text-sm font-medium text-amber-800">
            Nieuwe versie beschikbaar
          </p>
          <p className="text-xs text-amber-600 mt-1">
            Herlaad de pagina voor de laatste updates.
          </p>
          <button
            onClick={handleReload}
            className="mt-2 text-xs font-medium text-amber-700 hover:text-amber-900 underline"
          >
            Nu herladen →
          </button>
        </div>
        <button 
          onClick={() => setShowBanner(false)}
          className="text-amber-400 hover:text-amber-600"
          aria-label="Sluiten"
        >
          ×
        </button>
      </div>
    </div>
  );
};

export default VersionChecker;
